/**
 * 大会設定画面シーン
 *
 * 3ステップで大会を設定する:
 * 1. 大会方式選択（総当たり / トーナメント）
 * 2. キャラクター選択
 * 3. CPU難易度選択
 *
 * 完了後、TournamentBracketSceneへ遷移する。
 */
import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import { TournamentMode } from '../types/Tournament';
import { MONSTER_DATABASE, HIDDEN_MONSTER_IDS } from '../constants/monsters';
import { CPU_DIFFICULTY_OPTIONS } from './difficultySelectConfig';
import { loadGameProgress } from '../utils/gameProgressManager';
import { AILevel } from '../ai/types';
import { TOURNAMENT_SETUP_LABELS, TOURNAMENT_SETUP_LAYOUT } from './tournamentSetupConfig';
import { BackgroundImageKey } from '../constants/imageKeys';
import { createTournament } from '../tournament/tournamentManager';
import { loadCustomAICharacters } from '../utils/customAIManager';
import { AIConfig } from '../ai/types';
import { CustomAICharacter } from '../types/CustomAICharacter';

type SetupStep = 'type' | 'character' | 'difficulty';

export class TournamentSetupScene extends BaseScene {
  private currentStep: SetupStep = 'type';
  private selectedMode?: TournamentMode;
  private selectedMonsterId?: string;
  private selectedPlayerAIConfig?: AIConfig;
  private uiElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super(SceneKey.TOURNAMENT_SETUP);
  }

  create(): void {
    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE).setOrigin(0.5);
    }
    this.currentStep = 'type';
    this.selectedMode = undefined;
    this.selectedMonsterId = undefined;
    this.selectedPlayerAIConfig = undefined;
    this.showTypeSelection();
  }

  private clearUI(): void {
    this.uiElements.forEach((el) => el.destroy());
    this.uiElements = [];
  }

  private showTypeSelection(): void {
    this.clearUI();
    this.currentStep = 'type';

    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, TOURNAMENT_SETUP_LAYOUT.titleY, TOURNAMENT_SETUP_LABELS.title, {
          fontSize: '40px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5),
    );

    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, TOURNAMENT_SETUP_LAYOUT.subtitleY, TOURNAMENT_SETUP_LABELS.selectType, {
          fontSize: '20px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5),
    );

    const buttons = [
      {
        label: TOURNAMENT_SETUP_LABELS.roundRobin,
        desc: TOURNAMENT_SETUP_LABELS.roundRobinDesc,
        action: () => {
          this.selectedMode = TournamentMode.ROUND_ROBIN;
          this.showCharacterSelection();
        },
      },
      {
        label: TOURNAMENT_SETUP_LABELS.elimination,
        desc: TOURNAMENT_SETUP_LABELS.eliminationDesc,
        action: () => {
          this.selectedMode = TournamentMode.SINGLE_ELIMINATION;
          this.showCharacterSelection();
        },
      },
    ];

    buttons.forEach((btn, i) => {
      const y = TOURNAMENT_SETUP_LAYOUT.buttonStartY + i * 100;
      this.createLargeButton(GAME_WIDTH / 2, y, btn.label, btn.desc, btn.action);
    });

    this.createBackBtn(() => this.transitionTo(SceneKey.BATTLE_MODE_SELECT));
  }

  private showCharacterSelection(): void {
    this.clearUI();
    this.currentStep = 'character';

    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, TOURNAMENT_SETUP_LAYOUT.titleY, TOURNAMENT_SETUP_LABELS.title, {
          fontSize: '40px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5),
    );

    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, TOURNAMENT_SETUP_LAYOUT.subtitleY, TOURNAMENT_SETUP_LABELS.selectCharacter, {
          fontSize: '20px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5),
    );

    const cols = TOURNAMENT_SETUP_LAYOUT.charColumns;
    const colWidth = TOURNAMENT_SETUP_LAYOUT.charColumnWidth;
    const startX = GAME_WIDTH / 2 - (cols - 1) * colWidth / 2;

    const { unlockedHiddenMonsters } = loadGameProgress();
    const selectableMonsters = MONSTER_DATABASE.filter(
      (m) => !HIDDEN_MONSTER_IDS.includes(m.id) || unlockedHiddenMonsters.includes(m.id),
    );

    selectableMonsters.forEach((monster, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * colWidth;
      const y = TOURNAMENT_SETUP_LAYOUT.charButtonStartY + row * TOURNAMENT_SETUP_LAYOUT.charButtonSpacing;

      const text = this.add
        .text(x, y, monster.name, {
          fontSize: '20px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => { text.setColor('#ffffff'); text.setScale(1.1); });
      text.on('pointerout', () => { text.setColor('#ffffff'); text.setScale(1.0); });
      text.on('pointerdown', () => {
        this.selectedMonsterId = monster.id;
        this.selectedPlayerAIConfig = undefined;
        this.showDifficultySelection();
      });

      this.uiElements.push(text);
    });

    // カスタムAIキャラセクション
    const customAIs = loadCustomAICharacters();
    if (customAIs.length > 0) {
      const sectionY = TOURNAMENT_SETUP_LAYOUT.charButtonStartY +
        Math.ceil(selectableMonsters.length / cols) * TOURNAMENT_SETUP_LAYOUT.charButtonSpacing + 10;

      this.uiElements.push(
        this.add.text(GAME_WIDTH / 2, sectionY, '── カスタムAIキャラ ──', {
          fontSize: '15px',
          color: '#aaaacc',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        }).setOrigin(0.5),
      );

      customAIs.forEach((char: CustomAICharacter, i: number) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const x = startX + col * colWidth;
        const y = sectionY + 30 + row * 40;

        const text = this.add
          .text(x, y, `${char.name}（AI）`, {
            fontSize: '18px',
            color: '#88ccff',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
            stroke: '#000000',
            strokeThickness: 4,
          })
          .setOrigin(0.5)
          .setInteractive({ useHandCursor: true });

        text.on('pointerover', () => { text.setScale(1.1); });
        text.on('pointerout', () => { text.setScale(1.0); });
        text.on('pointerdown', () => {
          this.selectedMonsterId = char.monsterId;
          this.selectedPlayerAIConfig = char.aiConfig;
          this.showDifficultySelection();
        });

        this.uiElements.push(text);
      });
    }

    this.createBackBtn(() => this.showTypeSelection());
  }

  private showDifficultySelection(): void {
    this.clearUI();
    this.currentStep = 'difficulty';

    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, TOURNAMENT_SETUP_LAYOUT.titleY, TOURNAMENT_SETUP_LABELS.title, {
          fontSize: '40px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5),
    );

    this.uiElements.push(
      this.add
        .text(GAME_WIDTH / 2, TOURNAMENT_SETUP_LAYOUT.subtitleY, TOURNAMENT_SETUP_LABELS.selectDifficulty, {
          fontSize: '20px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5),
    );

    CPU_DIFFICULTY_OPTIONS.forEach((option, i) => {
      const y = TOURNAMENT_SETUP_LAYOUT.difficultyStartY + i * 60;
      const text = this.add
        .text(GAME_WIDTH / 2, y, option.label, {
          fontSize: '28px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => { text.setColor('#ffffff'); text.setScale(1.1); });
      text.on('pointerout', () => { text.setColor('#ffffff'); text.setScale(1.0); });
      text.on('pointerdown', () => {
        this.startTournament(option.aiLevel);
      });

      this.uiElements.push(text);
    });

    this.createBackBtn(() => this.showCharacterSelection());
  }

  private startTournament(aiLevel: AILevel): void {
    if (!this.selectedMode || !this.selectedMonsterId) return;

    const tournamentState = createTournament(
      this.selectedMode,
      this.selectedMonsterId,
      aiLevel,
      this.selectedPlayerAIConfig,
    );

    this.transitionTo(SceneKey.TOURNAMENT_BRACKET, { tournamentState });
  }

  private createLargeButton(x: number, y: number, label: string, desc: string, action: () => void): void {
    const labelText = this.add
      .text(x, y, label, {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    const descText = this.add
      .text(x, y + 30, desc, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    labelText.on('pointerover', () => { labelText.setColor('#ffffff'); labelText.setScale(1.1); });
    labelText.on('pointerout', () => { labelText.setColor('#ffffff'); labelText.setScale(1.0); });
    labelText.on('pointerdown', action);

    this.uiElements.push(labelText, descText);
  }

  private createBackBtn(action: () => void): void {
    const text = this.add
      .text(GAME_WIDTH / 2, TOURNAMENT_SETUP_LAYOUT.backButtonY, TOURNAMENT_SETUP_LABELS.back, {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    text.on('pointerover', () => { text.setColor('#ffffff'); text.setScale(1.1); });
    text.on('pointerout', () => { text.setColor('#ffffff'); text.setScale(1.0); });
    text.on('pointerdown', action);

    this.uiElements.push(text);
  }
}
